
import AP from '../config/api.config'
import store from '../store'
import PlatformLogo from '../components/common/PlatformLogo.vue'
import HumanVerification from '../components/common/HumanVerification.vue'
import { fetchPublicConfig } from '../utils/publicConfig'
import { postLoginHome } from '../utils/postLoginHome'

// 手机号格式与后端 normalizePhone 同规则：1[3-9] 开头 11 位（前端仅 UX 预检，服务端仍权威校验）
const PHONE_RE = /^1[3-9]\d{9}$/

// 密码强度前端预检：与后端 canonical（server/utils/passwordPolicy.js）同规则，双轨——
//   客户轨 role='user'：8-20 位，须同时含字母和数字（注册/改密/重置的对外口径）
//   员工轨 staff：≥6 位，须同时含数字、小写、大写（admin/operator/courier/super_admin 历史口径）
// 重置流程不暴露账号角色，故前端只做「任一轨通过即放行」的宽松预检，真正的角色路由判定
// 与最终边界在后端 policyCheck(role)：员工口令不会被客户轨挡住，客户口令也不会被前端放行到后端。
const CUSTOMER_PASSWORD_RE = /^(?=.*[A-Za-z])(?=.*\d).{8,20}$/
const STAFF_PASSWORD_RE = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{6,}$/
const PASSWORD_HINT = '密码需为8-20位，且同时包含字母和数字'

export default {
  name: 'ForgotPassword',
  components: { PlatformLogo, HumanVerification },
  data () {
    return {
      step: 1,
      stepLabels: ['验证手机号', '确认发送', '短信验证', '设置新密码', '进入系统'],
      phone: '',
      csid: '',          // client_session_id（HV 凭证三绑定之一；sessionStorage 持久，与注册链同语义）
      pendingPhone: '',  // 发起「下一步」时的手机号快照——拼图期间输入框不可改，check 以此为准
      hvDialog: false,   // 拼图弹窗开关（v-if 销毁组件 = 关闭即清理监听/滚动锁）
      resetFlowToken: '', // check 通过后的一次性流程凭证（send-code 唯一入参，服务端据此推导账号）
      smsCode: '',
      resetToken: '',
      newPassword: '',
      confirmPassword: '',
      showPwd: false,
      checking: false,
      sending: false,
      verifying: false,
      resetting: false,
      smsCountdown: 0,
      sectionText: '',
      tipOk: false,
      logoUrl: '',
      autoLoginPending: false // 第 5 步文案分流：true=自动登录成功正在进系统；false=需回登录页
    }
  },
  computed: {
    // 与后端 sms.service maskPhone 同规则：138****0001
    maskedPhone () {
      return this.phone.length === 11 ? this.phone.slice(0, 3) + '****' + this.phone.slice(7) : this.phone
    }
  },
  created () {
    fetchPublicConfig().then(cfg => { this.logoUrl = cfg.logoUrl })
    // 已登录用户不需要找回密码（与注册页同规则）
    if (localStorage.getItem('token')) {
      this.$router.push('/home')
      return
    }
    // client_session_id：与注册链同语义（sessionStorage 刷新不丢、关页即失效）。
    // HV session（csid+phone 绑定 1800s 滑动续期）靠它实现「同号重验免重拼图」的服务端通道。
    let sid = sessionStorage.getItem('pwreset_client_session_id')
    if (!sid) {
      sid = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : 'pwreset-' + Date.now() + '-' + Math.random().toString(36).slice(2)
      sessionStorage.setItem('pwreset_client_session_id', sid)
    }
    this.csid = sid
  },
  beforeDestroy () {
    if (this._smsTimer) clearInterval(this._smsTimer)
    if (this._enterTimer) clearTimeout(this._enterTimer)
  },
  methods: {
    toLogin () {
      this.$router.push({ path: '/' })
    },
    say (text, ok) {
      this.sectionText = text
      this.tipOk = !!ok
    },
    startCountdown (sec) {
      this.smsCountdown = sec || 60
      if (this._smsTimer) clearInterval(this._smsTimer)
      this._smsTimer = setInterval(() => {
        this.smsCountdown--
        if (this.smsCountdown <= 0) {
          clearInterval(this._smsTimer)
          this._smsTimer = null
        }
      }, 1000)
    },
    // 第 1 步「下一步」：前端格式预检 → 弹滑动拼图（本步绝不发短信）。拼图完成后由
    // onHvVerified 携一次性 hv_token 自动调 check；check 语义见 docs/AUTH_ENUMERATION_AUDIT.md
    requestNext () {
      const phone = (this.phone || '').replace(/\D/g, '').replace(/^86/, '')
      if (!PHONE_RE.test(phone)) {
        this.say('请输入正确的手机号')
        return
      }
      if (this.checking || this.hvDialog) return // 防重复点击（check 进行中与弹窗期双闸）
      this.pendingPhone = phone
      this.hvDialog = true
    },
    // check（HV 拼图门禁 password_reset 场景 → 限流 → 反枚举三态同响应）。
    // 不存在 / 不可自助 / 可用 → 一律 200 + ok:true + reset_flow_token，前端不区分（不揭示存在性）；
    // hv_token 在此一次性消费，成功即写 hv:session（csid+phone 绑定）——同号重验走 session 通道免重拼图。
    async doCheck (hvToken) {
      this.checking = true
      try {
        const res = await this.axios.post(AP.apiList.pwresetCheck, {
          phone: this.pendingPhone,
          client_session_id: this.csid,
          hv_token: hvToken || undefined
        })
        const d = res.data && res.data.data
        if (res.data.code === 200 && d && d.ok && d.reset_flow_token) {
          this.phone = this.pendingPhone
          this.resetFlowToken = d.reset_flow_token
          this.step = 2
          this.say('')
        } else {
          // 仅真实失败（429 限流 / 503 服务不可用）：后端文案即提示，留在本步允许改手机号
          this.say((res.data && res.data.message) || '验证失败，请重试')
        }
      } catch (err) {
        const msg = (err.response && err.response.data && err.response.data.message) || ''
        this.say(msg || '验证失败，请稍后重试')
      } finally {
        this.checking = false
      }
    },
    // ===== 拼图弹窗回调 =====
    async onHvVerified (token) {
      // 组件契约：换题/换号/复位时也 emit verified('')（清旧态信号），仅验证成功才带真实 token——
      // 空 token 是复位信号，忽略（弹窗保持，不触发 check）
      if (!token) return
      // 拼图通过：关弹窗 → 自动续跑 check（携一次性 hv_token）→ 200 进第 2 步，失败停留第 1 步
      this.hvDialog = false
      await this.doCheck(token)
    },
    onHvRequired (v) {
      // 服务端裁决该场景无需 HV（如运维翻转 config/整体下线）：关弹窗直接走 check（必放行）
      if (v === false && this.hvDialog) {
        this.hvDialog = false
        this.doCheck('')
      }
    },
    closeHvDialog () {
      // 用户主动返回：不触发 check、不消费任何凭证；组件随 v-if 销毁自清监听，q-dialog 滚动锁随 hide 释放
      this.hvDialog = false
      this.say('')
    },
    // 第 2 步「获取短信验证码」：只传 reset_flow_token，服务端推导账号（不重传手机号）
    // 反枚举：可投递与不可投递（不存在/不可自助）返回逐字段同构响应，因此**统一展示后端文案**
    // （中性条件句「如果该手机号已注册，我们将发送验证码」），前端不再假设"一定已发出"
    async sendCode () {
      if (!this.resetFlowToken) {
        this.step = 1
        return
      }
      this.sending = true
      try {
        const res = await this.axios.post(AP.apiList.pwresetSendCode, {
          reset_flow_token: this.resetFlowToken
        })
        if (res.data.code === 200 && res.data.data && res.data.data.expires_in && !res.data.data.reason) {
          const d = res.data.data
          this.startCountdown(d.resend_after || 60)
          this.step = 3
          if (d.dev_code) {
            // dev 联调：验证码自动填入（生产无此字段）
            this.smsCode = String(d.dev_code)
            this.say('【开发模式】验证码已自动填入', true)
          } else {
            // 中性文案单一真源 = 后端 message（存在/不存在手机号逐字一致）
            this.say((res.data && res.data.message) || '如果该手机号已注册，我们将发送验证码', true)
          }
        } else {
          // 发送失败（token 已消费，无论何种失败都必须回第 1 步重新走 check）
          this.say((res.data && res.data.message) || '短信发送失败，请稍后重试。')
          this.resetFlowToken = ''
          this.step = 1
        }
      } catch (err) {
        const status = err.response && err.response.status
        const msg = (err.response && err.response.data && err.response.data.message) || ''
        this.say(status === 429 ? (msg || '操作过于频繁，请稍后再试') : (msg || '短信发送失败，请稍后重试。'))
        this.resetFlowToken = ''
        this.step = 1 // token 一次性已消费，回第 1 步重新验证
      } finally {
        this.sending = false
      }
    },
    backToStep1 () {
      if (this.step === 3 && this.smsCountdown > 0) return // 倒计时内不允许（真实限制在服务端 60s）
      this.step = 1
      this.smsCode = ''
      this.resetFlowToken = '' // 旧 token 可能已消费/过期，重新走 check（换号则旧 HV session 不匹配）
      this.say('')
    },
    async verifyCode () {
      if (!(this.smsCode || '').trim()) {
        this.say('请输入短信验证码')
        return
      }
      this.verifying = true
      try {
        const res = await this.axios.post(AP.apiList.pwresetVerifyCode, {
          phone: this.phone,
          code: (this.smsCode || '').trim()
        })
        if (res.data.code === 200 && res.data.data && res.data.data.reset_token) {
          this.resetToken = res.data.data.reset_token
          this.step = 4
          this.say('')
        } else {
          this.say(res.data.message || '验证失败，请重试')
        }
      } catch (err) {
        this.say((err.response && err.response.data && err.response.data.message) || '验证失败，请重试')
      } finally {
        this.verifying = false
      }
    },
    async doReset () {
      // 强度预检：客户轨或员工轨任一通过即放行（角色路由与最终判定在后端 policyCheck）
      if (!this.newPassword) {
        this.say('请输入新密码')
        return
      }
      if (!CUSTOMER_PASSWORD_RE.test(this.newPassword) && !STAFF_PASSWORD_RE.test(this.newPassword)) {
        this.say(PASSWORD_HINT)
        return
      }
      if (this.newPassword !== this.confirmPassword) {
        this.say('两次输入的密码不一致')
        return
      }
      this.resetting = true
      try {
        const res = await this.axios.post(AP.apiList.pwresetReset, {
          reset_token: this.resetToken,
          new_password: this.newPassword
        })
        if (res.data.code === 200) {
          const d = res.data.data || {}
          // 重置成功即登录：后端已复用 _buildLoginState 签发 access + refresh session，
          // 前端只负责「写进现有 auth store + 跳转」，不自行造 token/session。
          if (d.token && d.user) {
            this.enterAfterReset(d)
          } else {
            // 后端自动登录失败（reset_ok_only）：密码已改成功，退回登录页用新密码登录
            this.autoLoginPending = false
            this.step = 5
            this.say(res.data.message || '密码重置成功，请使用新密码登录', true)
          }
        } else {
          this.say(res.data.message || '重置失败，请重试')
        }
      } catch (err) {
        this.say((err.response && err.response.data && err.response.data.message) || '重置失败，请重试')
      } finally {
        this.resetting = false
      }
    },
    // 重置成功后的登录态落地：与 loginpage.vue 登录成功逐项同口径（两份 localStorage + Vuex
    // setUser + 购物车角标 + 按角色落主页）——缺任一项就会出「store 有值但刷新掉登录」的半态
    enterAfterReset (d) {
      localStorage.setItem('token', d.token)
      localStorage.setItem('user', JSON.stringify(d.user))
      store.commit('setUser', d.user)
      store.dispatch('refreshCartCount')
      this.autoLoginPending = true
      this.step = 5
      this.say('')
      // 过渡页只为步骤条收尾（毫秒级），随即 replace 进系统：不留历史，返回键不会回到重置页
      const target = postLoginHome(d.user)
      this._enterTimer = setTimeout(() => { this.$router.replace(target) }, 600)
    }
  }
}
